import React, {useEffect, useState, useRef} from 'react';
import { useScaleData } from '../../hooks/useScaleData';

const ScaleConfig = ({ config, setConfig, devices, onConnect }) => {
	const [availableScales, setAvailableScales] = useState([]);
    const [availablePorts, setAvailablePorts] = useState([]);
    const [testing, setTesting] = useState(false);
    const [dualScaleMode, setDualScaleMode] = useState(config?.dualScaleMode || false);

    const { 
        currentWeight, 
        weightStatus,
        connecting,  // This exists in the hook
        setupScaleDataListener, 
        cleanupScaleDataListener,
        handleAutoConnect
    } = useScaleData(config?.scaleType, config);

	useEffect(() => {
		refreshScales();
        refreshSerialPorts();
		setupScaleDataListener();
		
		// Cleanup function
		return () => {
			cleanupScaleDataListener();
		};
	}, []);

    useEffect(() => {
        const handleScaleConnection = (isConnected) => {
            // console.log('Scale connection changed:', isConnected);
            if (isConnected) {
                // Scale connected - setup listener
                setupScaleDataListener();
            } else {
                // Scale disconnected - cleanup
                cleanupScaleDataListener();
            }
        };
    
        // Listen for scale connection events
        if (window.electronAPI?.on) {
            window.electronAPI.on('scale-connected', () => handleScaleConnection(true));
            window.electronAPI.on('scale-disconnected', () => handleScaleConnection(false));
        }
    
        return () => {
            if (window.electronAPI?.removeAllListeners) {
                window.electronAPI.removeAllListeners('scale-connected');
                window.electronAPI.removeAllListeners('scale-disconnected');
            }
        };
    }, [setupScaleDataListener, cleanupScaleDataListener]);

	const refreshScales = async () => {
		try {
			let scaleOptions = [];
			let ids = [];
			window.scaleAPI.listDevices().then(devices => {
				for (const device of devices) {
					let deviceId = device.vendorId + '_' + device.productId;
					if(!device.product || ids.includes(deviceId)) continue;
					scaleOptions.push({
						value: deviceId,
						label: device.product,
						description: ''
					});
					ids.push(deviceId);
				}
				setAvailableScales(scaleOptions);
				// console.log('Available scales:', availableScales);
			});
		} catch (error) {
			console.error('Failed to load scales:', error);
			setAvailableScales([]);
		}
	};

    const refreshSerialPorts = async () => {
        try {
            // This would be implemented in your electron API
            if (window.scaleAPI && window.scaleAPI.listSerialPorts) {
                const ports = await window.scaleAPI.listSerialPorts();
                setAvailablePorts(ports || []);
                // console.log('Available serial ports:', ports);
            }
        } catch (error) {
            console.error('Failed to load serial ports:', error);
            setAvailablePorts([]);
        }
    };

    const testScale = async () => {
        if (dualScaleMode) {
            // Test both scales in dual mode
            await testDualScales();
        } else {
            // Test single scale
            await testSingleScale();
        }
    };

    const testSingleScale = async () => {
        const isUSB = config.scaleType === 'usb';
        const isSerial = config.scaleType === 'serial';
        const isLoadRite = config.scaleType === 'loadrite';
        
        if (isUSB && !config.scaleDevice) {
            alert('Please select a USB scale device first');
            return;
        }
        
        if (isSerial && !config.serialPort) {
            alert('Please select a serial port first');
            return;
        }

        if (isLoadRite && (!config.loadriteAccessToken || !config.loadriteSiteId)) {
            alert('Please enter LoadRite access token and site ID first');
            return;
        }

        setTesting(true);
        try {
            if (isLoadRite) {
                // Test LoadRite API connection
                const testResult = await testLoadRiteConnection();
                if (testResult.success) {
                    alert(`LoadRite connection test successful!\nSite: ${config.loadriteSiteId}\nScale: ${config.loadriteScaleId || 'Default'}\nAPI Status: Connected`);
                } else {
                    throw new Error(testResult.error || 'Connection failed');
                }
            } else {
                // Simulate scale reading test for USB/Serial
                await new Promise(resolve => setTimeout(resolve, 1500));
                
                const testWeight = Math.floor(Math.random() * 50000) + 10000;
                
                // In a real implementation, this would communicate with the actual scale
                const deviceInfo = isUSB ? config.scaleDevice : `${config.serialPort} (${config.baudRate} baud)`;
                alert(`Scale test successful!\nDevice: ${deviceInfo}\nTest reading: ${testWeight.toLocaleString()} Ton(s)`);
            }
        } catch (error) {
            console.error('Scale test failed:', error);
            alert(`Scale test failed: ${error.message}\n\nPlease check your connection settings.`);
        } finally {
            setTesting(false);
        }
    };

    const testDualScales = async () => {
        setTesting(true);
        try {
            const results = [];
            
            // Test Empty Scale
            if (config.emptyScaleType) {
                const emptyResult = await testScaleConnection('empty', config.emptyScaleType, {
                    device: config.emptyScaleDevice,
                    port: config.emptySerialPort,
                    accessToken: config.emptyLoadriteAccessToken,
                    siteId: config.emptyLoadriteSiteId
                });
                results.push(`Empty Scale: ${emptyResult ? '✅ Connected' : '❌ Failed'}`);
            }
            
            // Test Full Scale
            if (config.fullScaleType) {
                const fullResult = await testScaleConnection('full', config.fullScaleType, {
                    device: config.fullScaleDevice,
                    port: config.fullSerialPort,
                    accessToken: config.fullLoadriteAccessToken,
                    siteId: config.fullLoadriteSiteId
                });
                results.push(`Full Scale: ${fullResult ? '✅ Connected' : '❌ Failed'}`);
            }
            
            alert(`Dual Scale Test Results:\n\n${results.join('\n')}`);
        } catch (error) {
            console.error('Dual scale test failed:', error);
            alert(`Dual scale test failed: ${error.message}`);
        } finally {
            setTesting(false);
        }
    };

    const testScaleConnection = async (scaleName, scaleType, config) => {
        try {
            if (scaleType === 'loadrite' && config.accessToken && config.siteId) {
                const response = await fetch(`https://apicloud.loadrite-myinsighthq.com/api/v2/context/get-health-check?Site=${config.siteId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${config.accessToken}`,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                return response.ok;
            } else if (scaleType === 'usb' && config.device) {
                // Simulate USB connection test
                await new Promise(resolve => setTimeout(resolve, 1000));
                return true;
            } else if (scaleType === 'serial' && config.port) {
                // Simulate serial connection test
                await new Promise(resolve => setTimeout(resolve, 1000));
                return true;
            } else if (scaleType === 'simulation') {
                // Simulation always works
                return true;
            }
            return false;
        } catch (error) {
            console.error(`${scaleName} scale test failed:`, error);
            return false;
        }
    };

    const testLoadRiteConnection = async () => {
        try {
            // Test LoadRite API connection by fetching health check
            const response = await fetch(`https://apicloud.loadrite-myinsighthq.com/api/v2/context/get-health-check?Site=${config.loadriteSiteId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${config.loadriteAccessToken}`,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                return { success: true, data };
            } else if (response.status === 401) {
                return { success: false, error: 'Invalid access token. Please check your token or refresh it.' };
            } else {
                return { success: false, error: `API Error: ${response.status} ${response.statusText}` };
            }
        } catch (error) {
            return { success: false, error: `Connection failed: ${error.message}` };
        }
    };

    const handleScaleTypeChange = (newType) => {
        // Disconnect current scale completely
        if (devices.scale.connected) {
            // onConnect('scale', { disconnect: true });
            cleanupScaleDataListener();
        }
        
        // Clear the config and set new type
        if (newType == 'usb') {
            setConfig({
                ...config,
                scaleType: newType,
                serialPort: '',
                baudRate: 9600,
                dataBits: 8,
                parity: 'none',
                stopBits: 1,
                flowControl: 'none',
                scaleDevice: newType
            });
        } else if (newType == 'serial') {
            setConfig({
                ...config,
                scaleType: newType,
                scaleDevice: newType,
                serialPort: '',
                baudRate: 9600,
                dataBits: 8,
                parity: 'none',
                stopBits: 1,
                flowControl: 'none'
            });
        } else if (newType == 'loadrite') {
            setConfig({
                ...config,
                scaleType: newType,
                scaleDevice: 'loadrite',
                serialPort: '',
                baudRate: 9600,
                dataBits: 8,
                parity: 'none',
                stopBits: 1,
                flowControl: 'none',
                loadriteAccessToken: '',
                loadriteRefreshToken: '',
                loadriteSiteId: '',
                loadriteScaleId: '',
                loadritePollingInterval: 5,
                loadriteDataType: 'NewWeight'
            });
        } else if (newType == 'simulation') {
            setConfig({
                ...config,
                scaleType: newType,
                scaleDevice: 'simulation',  // Add this so config has a device
                serialPort: '',
                baudRate: 9600,
                dataBits: 8,
                parity: 'none',
                stopBits: 1,
                flowControl: 'none'
            });
        }

        setupScaleDataListener();
    };

    const selectedUSBOption = availableScales.find(opt => opt.value === config.scaleDevice);
    const isUSBSelected = config.scaleType === 'usb';
    const isSerialSelected = config.scaleType === 'serial';
    const isSimulationSelected = config.scaleType === 'simulation';

    return (
        <div className="card">
            <h3><i className="fas fa-weight-hanging"></i> Scale Settings</h3>
            
            {/* Dual Scale Mode Toggle */}
            <div className="form-group">
                <label className="form-label">Scale Configuration</label>
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="radio"
                            name="scaleMode"
                            value="single"
                            checked={!dualScaleMode}
                            onChange={(e) => {
                                setDualScaleMode(false);
                                setConfig({...config, dualScaleMode: false});
                            }}
                        />
                        Single Scale
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="radio"
                            name="scaleMode"
                            value="dual"
                            checked={dualScaleMode}
                            onChange={(e) => {
                                setDualScaleMode(true);
                                setConfig({...config, dualScaleMode: true});
                            }}
                        />
                        Dual Scale (Empty/Full)
                    </label>
                </div>
                {dualScaleMode && (
                    <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
                        <p><strong>Dual Scale Mode:</strong> Configure two separate scales for weighing empty and full trucks.</p>
                        <p style={{ margin: '0.5rem 0 0 0' }}>
                            This is useful for locations with separate scales at entrance (empty) and exit (full).
                        </p>
                    </div>
                )}
            </div>

            {/* Single Scale Configuration */}
            {!dualScaleMode && (
                <>
                {/* Scale Type Selection */}
                <div className="form-group">
                    <label className="form-label">Scale Connection Type</label>
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                                type="radio"
                                name="scaleType"
                                value="usb"
                                checked={isUSBSelected}
                                onChange={(e) => handleScaleTypeChange(e.target.value)}
                            />
                            USB Scale
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                                type="radio"
                                name="scaleType"
                                value="serial"
                                checked={isSerialSelected}
                                onChange={(e) => handleScaleTypeChange(e.target.value)}
                            />
                            Serial Scale
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                                type="radio"
                                name="scaleType"
                                value="loadrite"
                                checked={config.scaleType === 'loadrite'}
                                onChange={(e) => handleScaleTypeChange(e.target.value)}
                            />
                            LoadRite Cloud
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                                type="radio"
                                name="scaleType"
                                value="simulation"
                                checked={isSimulationSelected}
                                onChange={(e) => handleScaleTypeChange(e.target.value)}
                            />
                            Simulation
                        </label>
                    </div>
                </div>

                    {/* USB Scale Configuration */}
                    {isUSBSelected && (
                        <div className="form-group">
                            <label className="form-label">USB Scale Device</label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <select
                            className="form-select"
                            value={config.scaleDevice || ''}
                            onChange={(e) => {
                                setConfig({...config, scaleDevice: e.target.value});
                            }}
                        >
                            <option value="">Select USB scale...</option>
                            {availableScales.map(option => (
                                <option
                                    key={option.value}
                                    value={option.value}
                                    disabled={option.disabled}
                                >
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <button
                            className="btn btn-secondary btn-small"
                            onClick={refreshScales}
                            title="Refresh scale list"
                        >
                            <i className="fas fa-sync"></i>
                        </button>
                    </div>
                    {selectedUSBOption && selectedUSBOption.description && (
                        <p style={{ fontSize: '0.875rem', color: '#718096', marginTop: '0.5rem' }}>
                            {selectedUSBOption.description}
                        </p>
                    )}
                </div>
            )}

            {/* Serial Scale Configuration */}
            {isSerialSelected && (
                <>
                    <div className="form-group">
                        <label className="form-label">Serial Port</label>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <select
                                className="form-select"
                                value={config.serialPort || ''}
                                onChange={(e) => {
                                    setConfig({...config, serialPort: e.target.value});
                                }}
                            >
                                <option value="">Select serial port...</option>
                                {availablePorts.map(port => (
                                    <option key={port.path} value={port.path}>
                                        {port.path} {port.friendlyName && `- ${port.friendlyName}`}
                                    </option>
                                ))}
                            </select>
                            <button
                                className="btn btn-secondary btn-small"
                                onClick={refreshSerialPorts}
                                title="Refresh serial ports"
                            >
                                <i className="fas fa-sync"></i>
                            </button>
                        </div>
                    </div>

                    <div className="grid-2" style={{ gap: '1rem' }}>
                        <div className="form-group">
                            <label className="form-label">Baud Rate</label>
                            <select
                                className="form-select"
                                value={config.baudRate || 9600}
                                onChange={(e) => {
                                    setConfig({...config, baudRate: parseInt(e.target.value)});
                                }}
                            >
                                <option value={1200}>1200</option>
                                <option value={2400}>2400</option>
                                <option value={4800}>4800</option>
                                <option value={9600}>9600</option>
                                <option value={19200}>19200</option>
                                <option value={38400}>38400</option>
                                <option value={57600}>57600</option>
                                <option value={115200}>115200</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Data Bits</label>
                            <select
                                className="form-select"
                                value={config.dataBits || 8}
                                onChange={(e) => {
                                    setConfig({...config, dataBits: parseInt(e.target.value)});
                                }}
                            >
                                <option value={7}>7</option>
                                <option value={8}>8</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Parity</label>
                            <select
                                className="form-select"
                                value={config.parity || 'none'}
                                onChange={(e) => {
                                    setConfig({...config, parity: e.target.value});
                                }}
                            >
                                <option value="none">None</option>
                                <option value="even">Even</option>
                                <option value="odd">Odd</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Stop Bits</label>
                            <select
                                className="form-select"
                                value={config.stopBits || 1}
                                onChange={(e) => {
                                    setConfig({...config, stopBits: parseInt(e.target.value)});
                                }}
                            >
                                <option value={1}>1</option>
                                <option value={2}>2</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Flow Control</label>
                        <select
                            className="form-select"
                            value={config.flowControl || 'none'}
                            onChange={(e) => {
                                setConfig({...config, flowControl: e.target.value});
                            }}
                        >
                            <option value="none">None</option>
                            <option value="xon/xoff">XON/XOFF</option>
                            <option value="rts/cts">RTS/CTS</option>
                        </select>
                    </div>
                </>
            )}

            {/* LoadRite Cloud Configuration */}
            {config.scaleType === 'loadrite' && (
                <>
                    <div className="form-group">
                        <label className="form-label">LoadRite API Configuration</label>
                        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
                            <p><strong>LoadRite Cloud Integration:</strong> Connect to LoadRite scales via their cloud API.</p>
                            <p style={{ margin: '0.5rem 0 0 0' }}>
                                This requires:
                            </p>
                            <ul style={{ paddingLeft: '1.5rem', margin: '0.5rem 0' }}>
                                <li>Valid LoadRite InsightHQ subscription</li>
                                <li>Manager level permissions</li>
                                <li>Active authorization token</li>
                            </ul>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Access Token</label>
                        <input
                            className="form-input"
                            type="password"
                            value={config.loadriteAccessToken || ''}
                            onChange={(e) => setConfig({...config, loadriteAccessToken: e.target.value})}
                            placeholder="Enter your LoadRite access token"
                        />
                        <p style={{ fontSize: '0.875rem', color: '#718096', marginTop: '0.5rem' }}>
                            Generate tokens from LoadRite InsightHQ with Manager permissions
                        </p>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Refresh Token</label>
                        <input
                            className="form-input"
                            type="password"
                            value={config.loadriteRefreshToken || ''}
                            onChange={(e) => setConfig({...config, loadriteRefreshToken: e.target.value})}
                            placeholder="Enter your LoadRite refresh token"
                        />
                        <p style={{ fontSize: '0.875rem', color: '#718096', marginTop: '0.5rem' }}>
                            Used for automatic token renewal (optional but recommended)
                        </p>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Site ID</label>
                        <input
                            className="form-input"
                            value={config.loadriteSiteId || ''}
                            onChange={(e) => setConfig({...config, loadriteSiteId: e.target.value})}
                            placeholder="Enter LoadRite site identifier"
                        />
                        <p style={{ fontSize: '0.875rem', color: '#718096', marginTop: '0.5rem' }}>
                            The LoadRite site identifier for your scale location
                        </p>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Scale ID</label>
                        <input
                            className="form-input"
                            value={config.loadriteScaleId || ''}
                            onChange={(e) => setConfig({...config, loadriteScaleId: e.target.value})}
                            placeholder="Enter LoadRite scale identifier"
                        />
                        <p style={{ fontSize: '0.875rem', color: '#718096', marginTop: '0.5rem' }}>
                            The specific LoadRite scale device identifier
                        </p>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Data Polling Interval (seconds)</label>
                        <select
                            className="form-select"
                            value={config.loadritePollingInterval || 5}
                            onChange={(e) => setConfig({...config, loadritePollingInterval: parseInt(e.target.value)})}
                        >
                            <option value={1}>1 second</option>
                            <option value={2}>2 seconds</option>
                            <option value={5}>5 seconds</option>
                            <option value={10}>10 seconds</option>
                            <option value={15}>15 seconds</option>
                            <option value={30}>30 seconds</option>
                        </select>
                        <p style={{ fontSize: '0.875rem', color: '#718096', marginTop: '0.5rem' }}>
                            How often to fetch new weight data from LoadRite API
                        </p>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Data Type</label>
                        <select
                            className="form-select"
                            value={config.loadriteDataType || 'NewWeight'}
                            onChange={(e) => setConfig({...config, loadriteDataType: e.target.value})}
                        >
                            <option value="NewWeight">New Weight (Real-time)</option>
                            <option value="Loading">Loading Events</option>
                            <option value="Conveyor">Conveyor Data</option>
                            <option value="Haul">Haul Data</option>
                            <option value="Positions">Position Data</option>
                        </select>
                        <p style={{ fontSize: '0.875rem', color: '#718096', marginTop: '0.5rem' }}>
                            Type of scale data to retrieve from LoadRite API
                        </p>
                    </div>
                </>
            )}

            {isSimulationSelected && (
                <div className="form-group">
                    <div className="alert alert-info">
                        <p><strong>Simulation Mode:</strong> The scale will generate random weight values for testing purposes.</p>
                        <p style={{ margin: '0.5rem 0 0 0' }}>
                            This mode is useful for:
                        </p>
                        <ul style={{ paddingLeft: '1.5rem', margin: '0.5rem 0' }}>
                            <li>Testing the application without physical hardware</li>
                            <li>Training and demonstrations</li>
                            <li>UI/UX development</li>
                        </ul>
                    </div>
                </div>
            )}

            {/* Live Weight Display */}
            {devices.scale.connected && (
                <div>
                    <h4 style={{ marginBottom: '1rem' }}>Live Weight Reading</h4>
                    <div 
                        className={`weight-display ${weightStatus === 'unstable' ? 'unstable' : ''}`}
                        style={{ 
                            fontSize: '2rem', 
                            minHeight: '4rem',
                            textAlign: 'center',
                            padding: '1rem',
                            border: '2px solid #e2e8f0',
                            borderRadius: '0.5rem',
                            backgroundColor: weightStatus === 'unstable' ? '#fef3c7' : '#f0f9ff',
                            color: weightStatus === 'unstable' ? '#92400e' : '#1e40af',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        {currentWeight || '0 g'}
                    </div>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginTop: '0.5rem',
                        fontSize: '0.875rem', 
                        color: '#718096' 
                    }}>
                        <span>
                            Status: <span style={{ 
                                color: weightStatus === 'stable' ? '#059669' : '#dc2626',
                                fontWeight: 'bold'
                            }}>
                                {weightStatus === 'stable' ? 'Stable' : 'Unstable'}
                            </span>
                        </span>
                        <span>Real-time updates</span>
                    </div>
                </div>
            )}
                </>
            )}

            {/* Dual Scale Configuration */}
            {dualScaleMode && (
                <>
                    {/* Empty Scale Configuration */}
                    <div className="card" style={{ marginBottom: '1rem', border: '2px solid #e2e8f0' }}>
                        <h4 style={{ marginBottom: '1rem', color: '#007bff' }}>
                            <i className="fas fa-truck"></i> Empty Scale (Entrance)
                        </h4>
                        <p style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '1rem' }}>
                            Scale used for weighing empty trucks at entrance
                        </p>
                        
                        {/* Empty Scale Type Selection */}
                        <div className="form-group">
                            <label className="form-label">Empty Scale Type</label>
                            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        name="emptyScaleType"
                                        value="usb"
                                        checked={config.emptyScaleType === 'usb'}
                                        onChange={(e) => setConfig({...config, emptyScaleType: e.target.value})}
                                    />
                                    USB Scale
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        name="emptyScaleType"
                                        value="serial"
                                        checked={config.emptyScaleType === 'serial'}
                                        onChange={(e) => setConfig({...config, emptyScaleType: e.target.value})}
                                    />
                                    Serial Scale
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        name="emptyScaleType"
                                        value="loadrite"
                                        checked={config.emptyScaleType === 'loadrite'}
                                        onChange={(e) => setConfig({...config, emptyScaleType: e.target.value})}
                                    />
                                    LoadRite Cloud
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        name="emptyScaleType"
                                        value="simulation"
                                        checked={config.emptyScaleType === 'simulation'}
                                        onChange={(e) => setConfig({...config, emptyScaleType: e.target.value})}
                                    />
                                    Simulation
                                </label>
                            </div>
                        </div>

                        {/* Empty Scale Configuration Details */}
                        {config.emptyScaleType === 'usb' && (
                            <div className="form-group">
                                <label className="form-label">Empty Scale USB Device</label>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <select
                                        className="form-select"
                                        value={config.emptyScaleDevice || ''}
                                        onChange={(e) => setConfig({...config, emptyScaleDevice: e.target.value})}
                                    >
                                        <option value="">Select USB scale...</option>
                                        {availableScales.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        className="btn btn-secondary btn-small"
                                        onClick={refreshScales}
                                        title="Refresh scale list"
                                    >
                                        <i className="fas fa-sync"></i>
                                    </button>
                                </div>
                            </div>
                        )}

                        {config.emptyScaleType === 'serial' && (
                            <div className="form-group">
                                <label className="form-label">Empty Scale Serial Port</label>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <select
                                        className="form-select"
                                        value={config.emptySerialPort || ''}
                                        onChange={(e) => setConfig({...config, emptySerialPort: e.target.value})}
                                    >
                                        <option value="">Select serial port...</option>
                                        {availablePorts.map(port => (
                                            <option key={port.path} value={port.path}>
                                                {port.path} {port.friendlyName && `- ${port.friendlyName}`}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        className="btn btn-secondary btn-small"
                                        onClick={refreshSerialPorts}
                                        title="Refresh serial ports"
                                    >
                                        <i className="fas fa-sync"></i>
                                    </button>
                                </div>
                            </div>
                        )}

                        {config.emptyScaleType === 'loadrite' && (
                            <div className="form-group">
                                <label className="form-label">Empty Scale LoadRite Configuration</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <input
                                        className="form-input"
                                        type="password"
                                        value={config.emptyLoadriteAccessToken || ''}
                                        onChange={(e) => setConfig({...config, emptyLoadriteAccessToken: e.target.value})}
                                        placeholder="Access Token"
                                    />
                                    <input
                                        className="form-input"
                                        value={config.emptyLoadriteSiteId || ''}
                                        onChange={(e) => setConfig({...config, emptyLoadriteSiteId: e.target.value})}
                                        placeholder="Site ID"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Full Scale Configuration */}
                    <div className="card" style={{ marginBottom: '1rem', border: '2px solid #e2e8f0' }}>
                        <h4 style={{ marginBottom: '1rem', color: '#28a745' }}>
                            <i className="fas fa-truck-loading"></i> Full Scale (Exit)
                        </h4>
                        <p style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '1rem' }}>
                            Scale used for weighing full trucks at exit
                        </p>
                        
                        {/* Full Scale Type Selection */}
                        <div className="form-group">
                            <label className="form-label">Full Scale Type</label>
                            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        name="fullScaleType"
                                        value="usb"
                                        checked={config.fullScaleType === 'usb'}
                                        onChange={(e) => setConfig({...config, fullScaleType: e.target.value})}
                                    />
                                    USB Scale
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        name="fullScaleType"
                                        value="serial"
                                        checked={config.fullScaleType === 'serial'}
                                        onChange={(e) => setConfig({...config, fullScaleType: e.target.value})}
                                    />
                                    Serial Scale
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        name="fullScaleType"
                                        value="loadrite"
                                        checked={config.fullScaleType === 'loadrite'}
                                        onChange={(e) => setConfig({...config, fullScaleType: e.target.value})}
                                    />
                                    LoadRite Cloud
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        name="fullScaleType"
                                        value="simulation"
                                        checked={config.fullScaleType === 'simulation'}
                                        onChange={(e) => setConfig({...config, fullScaleType: e.target.value})}
                                    />
                                    Simulation
                                </label>
                            </div>
                        </div>

                        {/* Full Scale Configuration Details */}
                        {config.fullScaleType === 'usb' && (
                            <div className="form-group">
                                <label className="form-label">Full Scale USB Device</label>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <select
                                        className="form-select"
                                        value={config.fullScaleDevice || ''}
                                        onChange={(e) => setConfig({...config, fullScaleDevice: e.target.value})}
                                    >
                                        <option value="">Select USB scale...</option>
                                        {availableScales.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        className="btn btn-secondary btn-small"
                                        onClick={refreshScales}
                                        title="Refresh scale list"
                                    >
                                        <i className="fas fa-sync"></i>
                                    </button>
                                </div>
                            </div>
                        )}

                        {config.fullScaleType === 'serial' && (
                            <div className="form-group">
                                <label className="form-label">Full Scale Serial Port</label>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <select
                                        className="form-select"
                                        value={config.fullSerialPort || ''}
                                        onChange={(e) => setConfig({...config, fullSerialPort: e.target.value})}
                                    >
                                        <option value="">Select serial port...</option>
                                        {availablePorts.map(port => (
                                            <option key={port.path} value={port.path}>
                                                {port.path} {port.friendlyName && `- ${port.friendlyName}`}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        className="btn btn-secondary btn-small"
                                        onClick={refreshSerialPorts}
                                        title="Refresh serial ports"
                                    >
                                        <i className="fas fa-sync"></i>
                                    </button>
                                </div>
                            </div>
                        )}

                        {config.fullScaleType === 'loadrite' && (
                            <div className="form-group">
                                <label className="form-label">Full Scale LoadRite Configuration</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <input
                                        className="form-input"
                                        type="password"
                                        value={config.fullLoadriteAccessToken || ''}
                                        onChange={(e) => setConfig({...config, fullLoadriteAccessToken: e.target.value})}
                                        placeholder="Access Token"
                                    />
                                    <input
                                        className="form-input"
                                        value={config.fullLoadriteSiteId || ''}
                                        onChange={(e) => setConfig({...config, fullLoadriteSiteId: e.target.value})}
                                        placeholder="Site ID"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Connection Status */}
            {devices.scale.connected && (
                <div className="alert alert-success">
                    <i className="fas fa-check-circle" style={{ color: '#28a745' }}></i> Connected to: {
                        config.scaleType === 'usb' ? config.scaleDevice : 
                        config.scaleType === 'loadrite' ? `LoadRite Cloud (Site: ${config.loadriteSiteId})` :
                        `${config.serialPort} (${config.baudRate} baud)`
                    }
                    {devices.scale.device && ` (${devices.scale.device})`}
                </div>
            )}

            {connecting && (
                <div className="alert alert-info">
                    <i className="fas fa-spinner fa-spin"></i> Connecting to scale...
                </div>
            )}

            {/* Test Scale Button */}
            {/* <div className="form-group" style={{ marginTop: '1rem' }}>
                <button
                    className="btn btn-primary"
                    onClick={testScale}
                    disabled={testing || (!dualScaleMode && !config.scaleType) || (dualScaleMode && !config.emptyScaleType && !config.fullScaleType)}
                >
                    {testing ? (
                        <>
                            <i className="fas fa-spinner fa-spin"></i> Testing...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-vial"></i> {dualScaleMode ? 'Test Both Scales' : 'Test Scale Connection'}
                        </>
                    )}
                </button>
                <p style={{ fontSize: '0.875rem', color: '#718096', marginTop: '0.5rem' }}>
                    {dualScaleMode ? 'Test the connection to both configured scales' : 'Test the connection to your configured scale'}
                </p>
            </div> */}

            <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#718096' }}>
                <p><strong>Note:</strong> Ensure your scale is properly calibrated and the communication interface is configured correctly.</p>
                {isSerialSelected && (
                    <p><strong>Serial Setup:</strong> Verify that your scale's serial settings match the configuration above. Common settings are 9600 baud, 8 data bits, no parity, 1 stop bit, and no flow control.</p>
                )}
            </div>
        </div>
    );
};

export default ScaleConfig;